import { ListKeyManager } from "@angular/cdk/a11y";
import { DOWN_ARROW, ENTER, SHIFT, TAB, UP_ARROW } from "@angular/cdk/keycodes";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostBinding,
	HostListener,
	inject,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	Output,
	QueryList,
	TemplateRef,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";

import { SearchKeyOptions } from "../../search/interfaces";
import { fuzzy } from "../../search/search.util";
import { KeyboardService } from "../../services/keyboard.service";

const SELECT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => SearchCellComponent),
	multi: true,
};

@Component({
	selector: "ic-search-cell",
	templateUrl: "./search-cell.component.html",
	providers: [SELECT_CONTROL_ACCESSOR],
	styleUrls: ["./search-cell.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SearchCellComponent implements ControlValueAccessor, OnInit, OnDestroy {
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly eRef = inject(ElementRef);
	private readonly fb = inject(UntypedFormBuilder);
	private readonly ngZone = inject(NgZone);
	private readonly keyBoarService = inject(KeyboardService);

	// Set containing all the selected items
	selected = new Set<any>();
	// Selected value or values needed for the formValue, influenced by the key property.
	value: any | any[];
	// Form used for the input search field.
	innerForm = this.fb.group({
		value: [""],
	});
	// Actual selected value, independent of the key property.
	selectedValue: any | any[];
	// Fallback for when placeholderTemplate is not included.
	fallbackPlaceholder: string;
	// Array of fuzzy filtered items.
	filteredItems: any[] = [];
	// Array of fuzzy filtered selected items.
	selectedItems: any[] = [];
	// Boolean to show the drop-down list.
	showList = false;
	// Manger for keyboard events.
	keyboardEventsManager: ListKeyManager<any>;
	// Current index for arrow-key navigation.
	itemIndex = -1;

	// Subject to track onDestroy event.
	private destroy$ = new Subject<void>();
	// Listener to handle outside click.
	private listener = this.onClick.bind(this);

	// Set of selected items, required to re-use the item-template in case the selected-template is not passed. */
	private selectedSet = new Set();
	// Array of items we'll filter.
	private toFilterItems: any[] = [];
	private focussed = false;
	private blurredFromGoingUp = false;

	//  The following inputs are standards used within the into.care input module.
	@Input() placeholder: string;
	@Input() errorText: string;
	// Optional key of the item we want to have as our formValue (i.e 'name' of {id: 1, name 'Hello'}).
	@Input() key?: string;
	// Optional searchKey string or strings of the item we want to match with (i.e ['fullname','name'] for [{id: 1, name: 'Hello'} {id: 1, fullname: 'Hello'}]).
	@Input() searchKey?: string | string[];
	// Optional searchKeyOptions to determine how we should match the searchKeys (either 'matchAll' or 'fallback')
	@Input() searchKeyOptions: SearchKeyOptions = { searchKeyType: "matchAll" };
	// Minimum amount of characters needed to start searching.
	@Input() minLength = 2;
	// Allow multiple items to be selected as a formValue.
	@Input() multi = false;
	// Items used in the searchList.
	@Input() items: any[] = [];

	// Output used in the action template.
	@Output() action = new EventEmitter<any>();
	@Output() blurEvent = new EventEmitter<void>();

	// Optional template to style the items in the list.
	@ContentChild("itemTmpl", { static: false }) itemTemplate?: TemplateRef<any>;
	// Optional template to style the placeholder of the selectedItems.
	@ContentChild("placeholderTmpl", { static: false }) placeholderTemplate?: TemplateRef<any>;
	// Optional template to style the selected items in the list.
	@ContentChild("selectedTmpl", { static: false }) selectedTemplate?: TemplateRef<any>;
	// Template to style the list when there are no items.
	@ContentChild("emptyTmpl", { static: false }) emptyTemplate: TemplateRef<any>;
	// Optional template to add an extra action add the bottom of the list.
	@ContentChild("actionTmpl", { static: false }) actionTemplate?: TemplateRef<any>;

	// Search input field.
	@ViewChild("input", { static: true }) input: ElementRef;
	// UL-element containing the filteredItems, selectedItems and the action.
	@ViewChild("list", { static: false }) list: ElementRef;
	// Manger for keyboard events.
	@ViewChildren("listItem") listItems: QueryList<any>;

	// Boolean indicating if control is disabled.
	@HostBinding("class.disabled") disabled = false;
	@HostBinding("attr.tabIndex") tabIndex = 0;
	@HostBinding("class.error") @Input() error = false;

	// Prevent default
	@HostListener("keyUp")
	keyUp() {
		return false;
	}

	@HostListener("focus")
	onFocus() {
		if (this.showList) {
			this.eRef.nativeElement.blur();
		}

		if (this.blurredFromGoingUp) {
			return;
		}

		this.toggle();
	}

	@HostListener("blur")
	onBlurred() {
		this.blurredFromGoingUp = false;
	}

	@HostListener("click")
	onClicked() {
		if (!this.blurredFromGoingUp) {
			return;
		}

		this.blurredFromGoingUp = false;
		this.toggle();
	}

	// Bind control functions.
	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onChange = (_: unknown) => {};

	/**
	 * Select or deselect item from the list.
	 *
	 * @param item - An item.
	 */
	selectOption(item: any) {
		if (!item) {
			return;
		}

		this.onTouch();
		const selected = this.key ? item[this.key] : item;

		if (this.multi) {
			if (this.selected.has(selected)) {
				this.selected.delete(selected);
				this.selectedSet.delete(item);
			} else {
				this.selected.add(selected);
				this.selectedSet.add(item);
			}
		} else {
			this.selected = new Set([selected]);
		}

		const result = Array.from(this.selected);

		this.value = this.multi ? result : result[0];

		// This will ensure no empty value can be sent to the formControl
		if (this.multi && this.value.length === 0) {
			this.value = null;
		}

		this.onChange(this.value);

		// Creates value to display the placeholder when needed
		if (this.placeholderTemplate) {
			this.selectedValue = this.multi ? Array.from(this.selectedSet) : item;
		} else {
			this.fallbackPlaceholder = this.multi ? result.join(", ") : result[0];
		}

		this.toggle();
		this.innerForm.reset();

		// Ensure that the items we'll filter do not include the currently selected items
		this.toFilterItems = this.items.filter((i) => !this.selected.has(this.key ? i[this.key] : i));
		this.filteredItems = this.toFilterItems;

		// Reset selectedItems to before we filtered
		this.selectedItems = Array.from(this.selectedSet);

		this.blurEvent.emit();

		this.cdRef.markForCheck();
	}

	// @Override
	registerOnChange(fn: (v: unknown) => void) {
		this.onChange = fn;
	}

	// @Override
	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	// @Override
	/**
	 * Set value when formValue already exists on init.
	 *
	 * @param value - An item or an array of items.
	 */
	writeValue(value: any | any[]) {
		this.value = value;
		this.selected = new Set(this.multi ? value : [value]);

		// Setup the selected item list for the visualization
		if (this.multi) {
			this.selectedSet = this.key
				? new Set(this.items.filter((item) => this.selected.has(item[this.key])))
				: new Set(value);
		}

		if (this.multi) {
			this.selectedValue = Array.from(this.selectedSet);
		} else {
			this.selectedValue = this.key ? this.items.find((i) => i[this.key] === value) : value;
		}

		this.toFilterItems = this.items.filter((item) => !this.selected.has(this.key ? item[this.key] : item));
		this.filteredItems = [...this.toFilterItems];
		this.selectedItems = Array.from(this.selectedSet);

		this.cdRef.markForCheck();
	}

	/**
	 * Disable control.
	 *
	 * @param isDisabled - Disable state.
	 */
	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;

		if (isDisabled) {
			this.innerForm.disable();
		} else {
			this.innerForm.enable();
		}
	}

	// Toggle the dropdown list.
	toggle() {
		if (this.disabled) {
			return;
		}

		this.itemIndex = -1;
		this.showList = !this.showList;

		if (this.showList) {
			this.listenToClick();
		} else {
			this.innerForm.reset();
			this.onTouch();
			this.blurEvent.emit();
		}

		this.cdRef.markForCheck();
	}

	// Emit the action when clicked.
	onAction() {
		this.action.emit();
		this.toggle();
	}

	// Set focus on input
	focus() {
		this.focussed = true;
		this.input.nativeElement.focus();
	}

	// Handle blur of list items
	handleBlur(index: number) {
		const shiftDown = this.keyBoarService.isDown(SHIFT);
		const tabbing = this.keyBoarService.isDown(TAB);
		if (tabbing && ((index === 0 && shiftDown) || (index === this.items.length - 1 && !shiftDown))) {
			this.blurredFromGoingUp = index === 0 && shiftDown;
			this.blurInput();
		}

		return;
	}

	blurInput() {
		this.itemIndex = -1;
		this.onTouch();
		this.toggle();
		this.blurEvent.emit();
	}

	setItemIndex(index: number) {
		this.itemIndex = index;
	}

	/**
	 * Handle keyUp event in order to work with keyboard navigation.
	 *
	 * @param event - Keyboard event.
	 */
	handleKeyUp(event: KeyboardEvent) {
		// Prevents auto-selecting the first item if focus was set by pressing enter on another item
		if (this.focussed) {
			this.focussed = false;

			return;
		}

		if (event.keyCode === DOWN_ARROW || event.keyCode === UP_ARROW) {
			this.navigate(event);
		} else if (event.keyCode === ENTER) {
			this.selectOption(this.findItemFromElement());
		} else {
			return;
		}

		event.stopPropagation();
		return false;
	}

	// Attach mouse-event listener.
	private listenToClick() {
		this.ngZone.runOutsideAngular(() => {
			window.addEventListener("click", this.listener);
		});
	}

	// Remove mouse-event listener.
	private removeClick() {
		window.removeEventListener("click", this.listener);
		this.innerForm.reset();
	}

	/**
	 * Handle outside clicks.
	 *
	 * @param event - Mouse event.
	 */
	private onClick(event: MouseEvent) {
		event.preventDefault();

		if (this.eRef.nativeElement.contains(event.target) || this.focussed) {
			// Prevents auto-selecting the first item if focus was set by clicking on another item
			this.focussed = false;

			return;
		}

		this.ngZone.run(() => {
			this.showList = false;
			this.onTouch();
			this.cdRef.markForCheck();
		});

		this.removeClick();
	}

	/**
	 * Navigate up or down the list of items.
	 *
	 * @param event - Keyboard event.
	 */
	private navigate(event: KeyboardEvent) {
		const isDown = event.keyCode === DOWN_ARROW;
		const newIndex = isDown ? this.itemIndex + 1 : this.itemIndex - 1;
		const items = this.listItems.toArray();

		if (newIndex < 0 || newIndex > items.length - 1) {
			return false;
		}

		const current = items[newIndex];
		const previous = isDown ? items[newIndex - 1] : items[newIndex + 1];

		if (current) {
			current.nativeElement.focus();
		}

		if (previous) {
			previous.nativeElement.blur();
		}
	}

	// Find the currently focussed on item in the list.
	private findItemFromElement() {
		const index = this.itemIndex === 0 ? this.itemIndex : this.itemIndex - 1;
		if (this.multi && index < this.selectedItems.length) {
			return this.selectedItems[index];
		}

		return this.filteredItems[index];
	}

	ngOnInit() {
		this.filteredItems = this.items;
		this.toFilterItems = this.items;

		this.innerForm
			.get("value")
			.valueChanges.pipe(
				tap((value) => {
					this.filteredItems = fuzzy(this.toFilterItems, value, this.searchKey, this.searchKeyOptions);
					this.selectedItems = fuzzy(Array.from(this.selectedSet), value, this.searchKey, this.searchKeyOptions);
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();

		this.keyboardEventsManager = new ListKeyManager(this.listItems);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
